import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import HeaderBar from './components/HeaderBar.jsx';
import Toolbar from './components/Toolbar.jsx';
import CircuitEdge from './components/CircuitEdge.jsx';
import CircuitNode from './components/CircuitNode.jsx';
import WireLayer from './components/WireLayer.jsx';
import PropertiesPanel from './components/PropertiesPanel.jsx';
import WirePropertiesPanel from './components/WirePropertiesPanel.jsx';
import HamiltonianPanel from './components/HamiltonianPanel.jsx';
import NodeLabelsPanel from './components/NodeLabelsPanel.jsx';
import HelpModal from './components/HelpModal.jsx';
import Tutorial from './components/Tutorial.jsx';
import SubmitModal from './components/SubmitModal.jsx';

const TUTORIAL_DISMISSED_KEY = 'quantum-circuit-builder:tutorial:dismissed:v1';

import {
  autosaveHasContent,
  clearAutosave,
  downloadJSON,
  loadAutosave,
  nextSchematicSymbol,
  parseImportPayload,
  saveAutosave,
  SCHEMATIC_DEFAULT,
} from './circuit/index.js';
import {
  EMPTY_WIRE,
  GRID,
  SNAP_RADIUS,
  WIRE_DEFAULT,
  WIRE_DEFAULT_ANALYSIS,
  addVertex as addWireVertex,
  addWire as addWireEdge,
  autoDetectNodes,
  canMergeVertexIntoWire,
  computeHover,
  deleteVertex as deleteWireVertex,
  deleteWire as deleteWireEdge,
  gluCoincidentVertices,
  mergeVertexIntoWire,
  addGround,
  beginGroundAt,
  mirrorSelection,
  moveComponentCenter,
  pasteSelection,
  placeStandaloneComponent,
  removeGround,
  setGroundOffset,
  snapGroundOffset,
  removeComponent,
  renumberComponentsOfType,
  rotateSelection,
  serializeSelection,
  setComponentColor,
  setComponentType,
  setComponentValue,
  snapToGrid,
  snapToVertex,
  snapToWire,
  splitWireAt,
  splitWireWithComponent,
} from './wire/index.js';

import { useHistory } from './hooks/useHistory.js';
import { usePanZoom } from './hooks/usePanZoom.js';
import { useResizablePanel } from './hooks/useResizablePanel.js';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts.js';

export default function App() {
  // Read autosave once at mount. Lazy useState initializers below pick
  // up loaded state; the autoDetect carry-forward (prevAnalysisRef) is
  // also primed from the loaded analysis so user-set node labels and
  // grounds aren't reset by the first auto-detect run after mount.
  const persisted = useState(() => {
    const p = loadAutosave();
    return autosaveHasContent(p) ? p : null;
  })[0];

  const [mode, setMode] = useState(() => persisted?.mode ?? 'wire');

  const [nodes, setNodes] = useState(() => persisted?.nodes ?? []);
  const [edges, setEdges] = useState(() => persisted?.edges ?? []);

  const [wire, setWire] = useState(() => persisted?.wire ?? WIRE_DEFAULT);
  const [wireNodes, setWireNodes] = useState(
    () => persisted?.wireNodes ?? WIRE_DEFAULT_ANALYSIS.nodes,
  );
  const [wireEdges, setWireEdges] = useState(
    () => persisted?.wireEdges ?? WIRE_DEFAULT_ANALYSIS.edges,
  );
  const [drawingFromVertexId, setDrawingFromVertexId] = useState(null);
  // Two-click ground placement: after the first click anchors to a
  // vertex, this holds { vertexId, x, y } until the second click sets
  // the ⏚ glyph's offset. Cleared by Escape, by switching tools, or
  // by leaving wire mode.
  const [placingGroundFor, setPlacingGroundFor] = useState(null);
  // Live cursor (and shift state) during ground placement, so the
  // ghost preview can rotate with the mouse without hooking into the
  // generic hover system. Only set while placingGroundFor is active.
  const [groundCursor, setGroundCursor] = useState(null);
  const [hover, setHover] = useState(null);

  // Selection model: an array of {kind/type, id} entries. Single-click
  // sets it to one entry; shift-click toggles entries in place.
  // `selected` is the convenience single-item view used by the
  // properties panel and other one-at-a-time UI.
  const [selection, setSelection] = useState([]);
  const selected = selection.length === 1 ? selection[0] : null;
  const setSelected = useCallback(
    (item) => setSelection(item ? [item] : []),
    [],
  );
  const toggleSelection = useCallback((item) => {
    setSelection((prev) => {
      const i = prev.findIndex(
        (s) =>
          s.id === item.id &&
          (s.kind ?? s.type) === (item.kind ?? item.type),
      );
      if (i >= 0) return prev.filter((_, idx) => idx !== i);
      return [...prev, item];
    });
  }, []);
  // In-memory clipboard. paste counter spreads multiple pastes
  // diagonally so they don't all stack at the same offset.
  const clipboardRef = useRef(null);
  const pasteCountRef = useRef(0);
  const [selectedTool, setSelectedTool] = useState(null);
  const [connectFrom, setConnectFrom] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [draggingComponentId, setDraggingComponentId] = useState(null);
  const [draggingGroundId, setDraggingGroundId] = useState(null);
  // Pulses the existing ⏚ on a node when the user tries to ground it
  // again. Cleared on a timer so the CSS animation can replay.
  const [flashingGroundId, setFlashingGroundId] = useState(null);
  const flashTimeoutRef = useRef(null);
  const flashGround = useCallback((id) => {
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    setFlashingGroundId(null);
    // requestAnimationFrame so React paints null before re-applying the
    // class — otherwise the same id back-to-back wouldn't restart the
    // animation.
    requestAnimationFrame(() => {
      setFlashingGroundId(id);
      flashTimeoutRef.current = setTimeout(() => {
        setFlashingGroundId(null);
        flashTimeoutRef.current = null;
      }, 700);
    });
  }, []);
  // Multi-drag: when a vertex / component that's part of a 2+ item
  // selection is grabbed, every selected vertex (plus endpoints of
  // selected wires/components) translates together. Snapshot of
  // start positions lives in a ref so per-frame updates don't churn
  // through React state.
  const [multiDragging, setMultiDragging] = useState(false);
  const multiDragRef = useRef(null);
  // Box-select rectangle. Started by Ctrl+drag on the canvas; on
  // release every vertex inside (and any wire/component whose both
  // endpoints are inside) joins the selection. With Shift held the
  // rectangle adds to the existing selection instead of replacing it.
  const [boxSelect, setBoxSelect] = useState(null);
  // Offset from cursor to component center at drag start (so the
  // component stays under the cursor at the same relative position).
  const componentDragOffset = useRef({ x: 0, y: 0 });
  const [theme, setTheme] = useState('dark');
  const [showEdgeLabels, setShowEdgeLabels] = useState(true);
  // When on, the on-canvas labels for grounded nodes are hidden.
  // The labels panel still shows the row so the user can edit it.
  // Default to on — the ⏚ glyph already identifies the node, and the
  // extra φ-label cluttered the canvas right next to it.
  const [hideGroundedLabels, setHideGroundedLabels] = useState(true);
  // When on, the colored dots marking wired vertex positions are
  // hidden. Hit-areas stay so the user can still click to select /
  // drag, and selection / hover states still light up the dot so the
  // user gets feedback during interaction.
  const [hideVertexDots, setHideVertexDots] = useState(false);
  const [highlightedNodeId, setHighlightedNodeId] = useState(null);
  const [highlightedComponentId, setHighlightedComponentId] = useState(null);
  const [focusedItem, setFocusedItem] = useState(null);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  // Help / tutorial overlays. The tutorial auto-opens on every load
  // until the user explicitly opts out via the "Don't show again"
  // checkbox. Just closing the tutorial keeps it returning next time
  // — that surprised users who expected it to stay available.
  const [helpOpen, setHelpOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(() => {
    try {
      return localStorage.getItem(TUTORIAL_DISMISSED_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const dismissTutorialForever = useCallback(() => {
    try {
      localStorage.setItem(TUTORIAL_DISMISSED_KEY, '1');
    } catch {
      // ignore
    }
  }, []);

  const handleHighlightNode = useCallback((id) => {
    setHighlightedNodeId(id);
    setHighlightedComponentId(null);
  }, []);

  const handleHighlightComponent = useCallback((id) => {
    setHighlightedComponentId(id);
    setHighlightedNodeId(null);
  }, []);

  const clearHighlights = useCallback(() => {
    setHighlightedNodeId(null);
    setHighlightedComponentId(null);
  }, []);

  const handleFocusItem = useCallback((kind, id) => {
    setFocusedItem({ kind, id });
  }, []);

  const handleBlurItem = useCallback(() => {
    setFocusedItem(null);
  }, []);

  const effectiveHighlightedNodeId =
    focusedItem?.kind === 'node' ? focusedItem.id : highlightedNodeId;
  const effectiveHighlightedComponentId =
    focusedItem?.kind === 'component' ? focusedItem.id : highlightedComponentId;

  const svgRef = useRef(null);
  const didDragRef = useRef(false);
  const nextNodeId = useRef(
    persisted?.nodes && persisted.nodes.length > 0
      ? Math.max(...persisted.nodes.map((n) => n.id)) + 1
      : 0,
  );
  const nextEdgeId = useRef(
    persisted?.edges && persisted.edges.length > 0
      ? Math.max(
          ...persisted.edges.map((e) => parseInt(String(e.id).slice(1), 10) || 0),
        ) + 1
      : 0,
  );

  const panZoom = usePanZoom(svgRef, { disabled: !!selectedTool });
  const panel = useResizablePanel();
  const leftPanel = useResizablePanel({ side: 'left' });

  const { undo, redo, canUndo, canRedo } = useHistory({
    nodes,
    edges,
    wire,
    wireNodes,
    wireEdges,
    dragging: dragging ?? draggingComponentId ?? (multiDragging ? 'multi' : null),
    setNodes,
    setEdges,
    setWire,
    setWireNodes,
    setWireEdges,
    onRestore: (s) => {
      setSelected(null);
      setDrawingFromVertexId(null);
      nextNodeId.current = s.nodes.length > 0 ? Math.max(...s.nodes.map((n) => n.id)) + 1 : 0;
      nextEdgeId.current =
        s.edges.length > 0
          ? Math.max(...s.edges.map((e) => parseInt(e.id.slice(1), 10))) + 1
          : 0;
    },
  });

  useEffect(() => {
    document.documentElement.classList.toggle('light', theme === 'light');
  }, [theme]);

  // ---- mode switch ----

  const handleSetMode = useCallback(
    (next) => {
      if (next === mode) return;
      setMode(next);
      setSelected(null);
      setConnectFrom(null);
      setDrawingFromVertexId(null);
      setPlacingGroundFor(null);
      setGroundCursor(null);
      setHover(null);
      setSelectedTool(next === 'wire' ? 'wire' : null);
      if (next === 'schematic' && nodes.length === 0 && edges.length === 0) {
        setNodes(SCHEMATIC_DEFAULT.nodes);
        setEdges(SCHEMATIC_DEFAULT.edges);
        nextNodeId.current = SCHEMATIC_DEFAULT.nextNodeId;
        nextEdgeId.current = SCHEMATIC_DEFAULT.nextEdgeId;
      }
    },
    [mode, nodes.length, edges.length],
  );

  // ---- shared actions ----

  const clearCircuit = useCallback(() => {
    if (mode === 'wire') {
      setWire(EMPTY_WIRE);
      setWireNodes([]);
      setWireEdges([]);
      setDrawingFromVertexId(null);
      setSelectedTool('wire');
    } else {
      setNodes([]);
      setEdges([]);
      setConnectFrom(null);
      setSelectedTool(null);
      nextNodeId.current = 0;
      nextEdgeId.current = 0;
    }
    setSelected(null);
    setHover(null);
    panZoom.reset();
  }, [panZoom, mode]);

  const updateNode = useCallback((id, patch) => {
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
  }, []);

  const updateEdge = useCallback((id, patch) => {
    setEdges((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const updateWireNode = useCallback((id, patch) => {
    const augmented = { ...patch };
    if (patch.label !== undefined) {
      augmented.userLabel = patch.label.length > 0;
    }
    if (patch.color !== undefined) {
      augmented.userColor = true;
    }
    setWireNodes((prev) => prev.map((n) => (n.id === id ? { ...n, ...augmented } : n)));
  }, []);

  const setAllNodeColors = useCallback((color) => {
    setWireNodes((prev) => prev.map((n) => ({ ...n, color, userColor: true })));
  }, []);

  const setAllComponentColorsOfType = useCallback((typeKey, color) => {
    setWire((w) => ({
      ...w,
      components: w.components.map((c) => (c.type === typeKey ? { ...c, color } : c)),
    }));
  }, []);

  // Recolor every component, regardless of type — used by the
  // labels-panel "match all components" picker.
  const setAllComponentColors = useCallback((color) => {
    setWire((w) => ({
      ...w,
      components: w.components.map((c) => ({ ...c, color, userColor: true })),
    }));
  }, []);

  // Reset every electrical-node label to \phi_{0..N-1}. Active nodes
  // are numbered first; grounded ones are pushed to the tail of the
  // range so they don't take up the low indices (they're eliminated
  // from the dynamical Hamiltonian anyway). Clearing userLabel lets
  // the next auto-detect run keep these as the auto-assigned ones.
  const relabelAllNodes = useCallback(() => {
    setWireNodes((prev) => {
      const active = [];
      const grounded = [];
      for (const n of prev) (n.isGround ? grounded : active).push(n);
      const ordered = [...active, ...grounded];
      const labelById = new Map();
      ordered.forEach((n, i) => labelById.set(n.id, `\\phi_{${i}}`));
      return prev.map((n) => ({
        ...n,
        label: labelById.get(n.id),
        userLabel: false,
      }));
    });
  }, []);

  const relabelComponentsOfType = useCallback((typeKey) => {
    setWire((w) => renumberComponentsOfType(w, typeKey));
  }, []);

  // Drag-reorder a row in the ELECTRICAL NODES list. Re-runs the auto
  // \phi_{k} assignment afterwards so indices follow the new display
  // order (active first, grounded last); user-set labels are kept.
  const reorderWireNodes = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setWireNodes((prev) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex >= prev.length
      ) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      const orderedForLabels = [
        ...next.filter((n) => !n.isGround),
        ...next.filter((n) => n.isGround),
      ];
      const used = new Set(
        next.filter((n) => n.userLabel && n.label).map((n) => n.label),
      );
      const labelById = new Map();
      let idx = 0;
      for (const n of orderedForLabels) {
        if (n.userLabel) continue;
        while (used.has(`\\phi_{${idx}}`)) idx++;
        const label = `\\phi_{${idx}}`;
        labelById.set(n.id, label);
        used.add(label);
        idx++;
      }
      return next.map((n) =>
        n.userLabel || !labelById.has(n.id)
          ? n
          : { ...n, label: labelById.get(n.id) },
      );
    });
  }, []);

  // Drag-reorder a row inside a component-type group (C / L / JJ).
  // Components of other types keep their relative positions; only the
  // moved type's slice is permuted within wire.components.
  const reorderComponentsOfType = useCallback((typeKey, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setWire((w) => {
      const ofType = w.components.filter((c) => c.type === typeKey);
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= ofType.length ||
        toIndex >= ofType.length
      ) {
        return w;
      }
      const reordered = [...ofType];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      let i = 0;
      const components = w.components.map((c) =>
        c.type === typeKey ? reordered[i++] : c,
      );
      return { ...w, components };
    });
  }, []);

  const mergeSelectedVertex = useCallback(() => {
    if (!selected || selected.kind !== 'wireVertex') return;
    setWire((w) => mergeVertexIntoWire(w, selected.id));
    setSelected(null);
  }, [selected]);

  const deleteSelected = useCallback(() => {
    if (selection.length === 0) return;
    // Schematic-mode entries (type: 'edge' / 'node') and wire-mode
    // entries (kind: 'wire*') can both appear in `selection`. Bucket
    // them so we apply each kind in one pass.
    const schEdges = selection.filter((s) => s.type === 'edge').map((s) => s.id);
    const schNodes = selection.filter((s) => s.type === 'node').map((s) => s.id);
    if (schEdges.length || schNodes.length) {
      const nodeSet = new Set(schNodes);
      const edgeSet = new Set(schEdges);
      setEdges((prev) =>
        prev.filter((e) => !edgeSet.has(e.id) && !nodeSet.has(e.from) && !nodeSet.has(e.to)),
      );
      if (schNodes.length) setNodes((prev) => prev.filter((n) => !nodeSet.has(n.id)));
    }

    const wireComps = selection.filter((s) => s.kind === 'wireComponent').map((s) => s.id);
    const wireWires = selection.filter((s) => s.kind === 'wire').map((s) => s.id);
    const wireVerts = selection.filter((s) => s.kind === 'wireVertex').map((s) => s.id);
    const wireGrounds = selection.filter((s) => s.kind === 'wireGround').map((s) => s.id);
    if (wireComps.length || wireWires.length || wireVerts.length || wireGrounds.length) {
      setWire((w) => {
        let next = w;
        for (const id of wireGrounds) next = removeGround(next, id);
        for (const id of wireComps) next = removeComponent(next, id);
        for (const id of wireWires) next = deleteWireEdge(next, id);
        for (const id of wireVerts) next = deleteWireVertex(next, id);
        return next;
      });
    }
    setSelection([]);
  }, [selection]);

  // ---- copy / paste / rotate ----

  const copySelection = useCallback(() => {
    if (mode !== 'wire' || selection.length === 0) return;
    clipboardRef.current = serializeSelection(wire, selection);
    pasteCountRef.current = 0;
  }, [mode, wire, selection]);

  const pasteClipboard = useCallback(() => {
    if (mode !== 'wire' || !clipboardRef.current) return;
    pasteCountRef.current += 1;
    const offset = pasteCountRef.current * GRID;
    // Compute the new wire eagerly so setWire and setSelection batch
    // as a single render. Calling setSelection inside a setWire
    // reducer is unsafe — reducers must be pure, and StrictMode
    // double-invokes them, which fired the side effect twice.
    const r = pasteSelection(wire, clipboardRef.current, offset, offset);
    setWire(r.wire);
    setSelection(r.selection);
  }, [mode, wire]);

  const rotateSelectionBy = useCallback(
    (clockwise) => {
      if (mode !== 'wire' || selection.length === 0) return;
      const angle = (clockwise ? 1 : -1) * (Math.PI / 2);
      setWire((w) => rotateSelection(w, selection, angle));
    },
    [mode, selection],
  );

  const mirrorSelectionAcross = useCallback(
    (axis) => {
      if (mode !== 'wire' || selection.length === 0) return;
      setWire((w) => mirrorSelection(w, selection, axis));
    },
    [mode, selection],
  );

  const handleSelectTool = useCallback((toolId) => {
    setSelectedTool((prev) => (prev === toolId ? null : toolId));
    setConnectFrom(null);
    setDrawingFromVertexId(null);
    setPlacingGroundFor(null);
    setGroundCursor(null);
    setHover(null);
  }, []);

  const handleEscape = useCallback(() => {
    if (placingGroundFor !== null) {
      setPlacingGroundFor(null);
      setGroundCursor(null);
    } else if (drawingFromVertexId !== null) {
      setDrawingFromVertexId(null);
    } else if (selectedTool) {
      setSelectedTool(null);
      setConnectFrom(null);
    } else if (selected) {
      setSelected(null);
    }
  }, [selectedTool, selected, drawingFromVertexId, placingGroundFor]);

  useKeyboardShortcuts({
    undo,
    redo,
    onEscape: handleEscape,
    onDelete: deleteSelected,
    onCopy: copySelection,
    onPaste: pasteClipboard,
    onRotate: rotateSelectionBy,
    onMirror: mirrorSelectionAcross,
  });

  // ---- wire-mode hover dispatch ----

  // Cache the id→vertex map per wire snapshot. snapToWire / snapToComponent
  // / computeHover all need it, and rebuilding it on every mousemove was
  // the hottest allocation in the hover path on large circuits.
  const vById = useMemo(
    () => new Map(wire.vertices.map((v) => [v.id, v])),
    [wire.vertices],
  );

  const hoverFor = useCallback(
    (pt, shiftKey) =>
      mode === 'wire' ? computeHover(wire, selectedTool, pt, shiftKey, vById) : null,
    [mode, selectedTool, wire, vById],
  );

  const handleWireClick = useCallback(
    (pt, shiftKey) => {
      const h = hoverFor(pt, shiftKey);

      if (selectedTool === 'wire') {
        let nextWire = wire;
        let targetVertexId = null;
        let snappedToExistingVertex = false;

        if (h?.kind === 'vertex') {
          targetVertexId = h.id;
          snappedToExistingVertex = true;
        } else if (h?.kind === 'wire') {
          const r = splitWireAt(nextWire, h.wireId, h.t);
          nextWire = r.wire;
          targetVertexId = r.vertexId;
        } else {
          const x = h?.x ?? pt.x;
          const y = h?.y ?? pt.y;
          const r = addWireVertex(nextWire, x, y);
          nextWire = r.wire;
          targetVertexId = r.vertexId;
        }

        if (drawingFromVertexId === null) {
          setWire(nextWire);
          setDrawingFromVertexId(targetVertexId);
        } else if (targetVertexId === drawingFromVertexId) {
          setWire(nextWire);
          setDrawingFromVertexId(null);
        } else {
          nextWire = addWireEdge(nextWire, drawingFromVertexId, targetVertexId);
          setWire(nextWire);
          // Snap to an existing vertex closes the chain; everything else
          // continues from the new vertex so the user can keep drawing.
          setDrawingFromVertexId(snappedToExistingVertex ? null : targetVertexId);
        }
        return;
      }

      if (selectedTool === 'C' || selectedTool === 'L' || selectedTool === 'JJ') {
        if (h?.kind === 'componentOnWire') {
          // Split the wire and insert the component edge in between.
          setWire((w) => splitWireWithComponent(w, h.wireId, h.t, selectedTool).wire);
        } else {
          // No wire under the cursor — drop a standalone component
          // with two free terminal vertices.
          const base = shiftKey ? pt : snapToGrid(pt.x, pt.y);
          setWire((w) => placeStandaloneComponent(w, base.x, base.y, selectedTool).wire);
        }
        return;
      }

      if (selectedTool === 'GND') {
        // Two-click placement. Click 1 anchors to a vertex (or splits
        // the wire under the cursor); click 2 sets where the ⏚ glyph
        // hangs relative to that vertex. Clicking a node that's
        // already grounded flashes the existing ⏚ red and refuses to
        // start placement — the user can use selection + Delete to
        // remove an existing ground instead.
        if (placingGroundFor === null) {
          // Locate the electrical node that would receive the ground.
          // Vertex hover → its owner node. Wire hover → either of the
          // wire's endpoints belongs to the same node (wires are
          // intra-node), so checking one suffices.
          let targetNodeId = null;
          if (h?.kind === 'vertex') {
            targetNodeId = wireNodes.find((n) =>
              n.vertexIds?.includes(h.id),
            )?.id ?? null;
          } else if (h?.kind === 'wire') {
            const w = wire.wires.find((e) => e.id === h.wireId);
            if (w) {
              targetNodeId = wireNodes.find((n) =>
                n.vertexIds?.includes(w.from) || n.vertexIds?.includes(w.to),
              )?.id ?? null;
            }
          }
          if (targetNodeId !== null) {
            const ownerNode = wireNodes.find((n) => n.id === targetNodeId);
            if (ownerNode?.isGround) {
              const existing = (wire.grounds ?? []).find((g) =>
                ownerNode.vertexIds?.includes(g.vertexId),
              );
              if (existing) flashGround(existing.id);
              return;
            }
          }
          const r = beginGroundAt(wire, pt.x, pt.y, shiftKey);
          if (r.wire !== wire) setWire(r.wire);
          if (r.vertexId === null) return;
          const anchor = r.wire.vertices.find((v) => v.id === r.vertexId);
          if (anchor) {
            setPlacingGroundFor({ vertexId: r.vertexId, x: anchor.x, y: anchor.y });
            setGroundCursor({ x: pt.x, y: pt.y, shiftKey });
          }
        } else {
          const { vertexId, x: ax, y: ay } = placingGroundFor;
          const off = snapGroundOffset(ax, ay, pt.x, pt.y, shiftKey);
          setWire((w) => addGround(w, vertexId, off.dx, off.dy));
          setPlacingGroundFor(null);
          setGroundCursor(null);
        }
        return;
      }

      // No tool — selection / deselection. Shift+click toggles into
      // a multi-selection without disturbing the existing items
      // (used as the source for copy / rotate / batch delete).
      let item = null;
      if (h?.kind === 'vertex') item = { kind: 'wireVertex', id: h.id };
      else if (h?.kind === 'component') item = { kind: 'wireComponent', id: h.id };
      else if (h?.kind === 'wire') item = { kind: 'wire', id: h.wireId };

      if (shiftKey) {
        if (item) toggleSelection(item);
        return;
      }

      if (item) {
        setSelected(item);
        if (item.kind === 'wireVertex') {
          const owner = wireNodes.find((n) => n.vertexIds?.includes(item.id));
          if (owner) handleHighlightNode(owner.id);
          else clearHighlights();
        } else if (item.kind === 'wireComponent') {
          handleHighlightComponent(item.id);
        } else if (item.kind === 'wire') {
          const w = wire.wires.find((e) => e.id === item.id);
          const owner = w
            ? wireNodes.find((n) => n.vertexIds?.includes(w.from))
            : null;
          if (owner) handleHighlightNode(owner.id);
          else clearHighlights();
        }
      } else {
        setSelected(null);
        clearHighlights();
      }
    },
    [
      hoverFor,
      selectedTool,
      wire,
      wireNodes,
      drawingFromVertexId,
      toggleSelection,
      setSelected,
      handleHighlightNode,
      handleHighlightComponent,
      clearHighlights,
    ],
  );

  // Auto-rerun electrical-node detection on every wire change. Uses a
  // ref for the previous result so the effect doesn't depend on
  // wireNodes/wireEdges (which would loop).
  const prevAnalysisRef = useRef({
    nodes: persisted?.wireNodes ?? [],
    edges: persisted?.wireEdges ?? [],
  });
  useEffect(() => {
    prevAnalysisRef.current = { nodes: wireNodes, edges: wireEdges };
  }, [wireNodes, wireEdges]);

  // Skip analysis while a drag is in progress — wire mutates every
  // mousemove during a drag and rerunning union-find + setting state
  // would cascade a re-render of the Hamiltonian panel each frame.
  // The effect refires when isDragging flips back to false, so the
  // post-drag wire state is always analyzed exactly once on release.
  const isDragging =
    dragging !== null ||
    draggingComponentId !== null ||
    draggingGroundId !== null ||
    multiDragging;

  useEffect(() => {
    if (isDragging) return;
    const result = autoDetectNodes(wire, prevAnalysisRef.current);
    setWireNodes(result.nodes);
    setWireEdges(result.edges);
  }, [wire, isDragging]);

  // Persist the document to localStorage on changes. Debounced so a
  // burst of edits collapses into a single write, and skipped during
  // drag so we're not stringifying the whole circuit every frame.
  useEffect(() => {
    if (isDragging) return;
    const id = setTimeout(() => {
      saveAutosave({ mode, nodes, edges, wire, wireNodes, wireEdges });
    }, 400);
    return () => clearTimeout(id);
  }, [mode, nodes, edges, wire, wireNodes, wireEdges, isDragging]);

  const handleImportFile = useCallback(
    async (file) => {
      let payload;
      try {
        const text = await file.text();
        payload = JSON.parse(text);
      } catch (err) {
        alert(`Could not parse JSON: ${err.message}`);
        return;
      }
      let parsed;
      try {
        parsed = parseImportPayload(payload);
      } catch (err) {
        alert(`Could not import circuit: ${err.message}`);
        return;
      }

      setSelected(null);
      setConnectFrom(null);
      setDrawingFromVertexId(null);
      setHover(null);

      if (parsed.kind === 'wire') {
        setMode('wire');
        // Land the user in selection mode after import — they almost
        // always want to inspect what they just loaded, not draw on
        // top of it.
        setSelectedTool(null);
        setWire(parsed.wire);
        const previousNodes = parsed.nodeOverrides.map((o) => ({
          label: o.label,
          color: o.color,
          userLabel: !!o.label,
          userColor: !!o.color,
          isGround: !!o.is_ground,
          vertexIds: o.anchor != null ? [o.anchor] : [],
        }));
        const result = autoDetectNodes(parsed.wire, { nodes: previousNodes, edges: [] });
        setWireNodes(result.nodes);
        setWireEdges(result.edges);
      } else {
        setMode('schematic');
        setSelectedTool(null);
        setNodes(parsed.nodes);
        setEdges(parsed.edges);
        nextNodeId.current = parsed.nextNodeId;
        nextEdgeId.current = parsed.nextEdgeId;
      }

      // Apply exported view state (theme, edge-label toggle, camera)
      // so the importer's screen matches what the sender saw. Each
      // field is optional — missing ones leave the importer's
      // current setting alone, so older JSONs still import cleanly.
      if (parsed.view) {
        if (parsed.view.theme === 'light' || parsed.view.theme === 'dark') {
          setTheme(parsed.view.theme);
        }
        if (typeof parsed.view.showEdgeLabels === 'boolean') {
          setShowEdgeLabels(parsed.view.showEdgeLabels);
        }
        if (parsed.view.pan || parsed.view.zoom != null) {
          panZoom.setView({ pan: parsed.view.pan, zoom: parsed.view.zoom });
        }
      }
    },
    [panZoom],
  );

  const onSetComponentType = useCallback((componentId, type) => {
    setWire((w) => setComponentType(w, componentId, type));
  }, []);

  const onSetComponentValue = useCallback((componentId, value) => {
    setWire((w) => setComponentValue(w, componentId, value));
  }, []);

  const onUpdateComponent = useCallback((componentId, patch) => {
    setWire((w) => {
      let next = w;
      if (patch.value !== undefined) next = setComponentValue(next, componentId, patch.value);
      if (patch.color !== undefined) next = setComponentColor(next, componentId, patch.color);
      if (patch.type !== undefined) next = setComponentType(next, componentId, patch.type);
      return next;
    });
  }, []);

  // ---- schematic-mode handlers ----

  const onSchematicCanvasClick = useCallback(
    (pt) => {
      if (selectedTool === 'node') {
        const id = nextNodeId.current++;
        setNodes((prev) => [
          ...prev,
          { id, x: pt.x, y: pt.y, label: `\\phi_{${id}}`, isGround: false },
        ]);
        return;
      }
      setSelected(null);
      setConnectFrom(null);
    },
    [selectedTool],
  );

  const onSchematicNodeClick = useCallback(
    (nodeId, e) => {
      e.stopPropagation();
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }
      if (selectedTool && selectedTool !== 'node') {
        if (connectFrom === null) {
          setConnectFrom(nodeId);
        } else if (connectFrom !== nodeId) {
          const id = `e${nextEdgeId.current++}`;
          setEdges((prev) => [
            ...prev,
            {
              id,
              from: connectFrom,
              to: nodeId,
              type: selectedTool,
              value: nextSchematicSymbol(prev, selectedTool),
            },
          ]);
          setConnectFrom(null);
        }
        return;
      }
      setSelected({ type: 'node', id: nodeId });
    },
    [selectedTool, connectFrom],
  );

  const onSchematicNodeMouseDown = useCallback((nodeId, e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setDragging(nodeId);
  }, []);

  const onSchematicEdgeClick = useCallback((edgeId, e) => {
    e.stopPropagation();
    setSelected({ type: 'edge', id: edgeId });
  }, []);

  // ---- canvas-level events ----

  const onCanvasClick = useCallback(
    (e) => {
      if (panZoom.didPanRef.current) {
        panZoom.didPanRef.current = false;
        return;
      }
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }
      const pt = panZoom.svgPoint(e);
      if (mode === 'wire') {
        handleWireClick(pt, e.shiftKey);
        return;
      }
      // Schematic: only handle background hits; nodes/edges have their own onClick.
      if (e.target !== svgRef.current && e.target.tagName !== 'rect') return;
      onSchematicCanvasClick(pt);
    },
    [mode, panZoom, handleWireClick, onSchematicCanvasClick],
  );

  const onMouseMove = useCallback(
    (e) => {
      if (panel.isResizing() || leftPanel.isResizing()) return;
      if (panZoom.processMove(e)) return;

      const pt = panZoom.svgPoint(e);

      if (dragging !== null) {
        didDragRef.current = true;
        if (mode === 'wire') {
          const base = e.shiftKey ? pt : snapToGrid(pt.x, pt.y);
          setWire((w) => {
            // Snap visually onto another vertex when within radius.
            const others = w.vertices.filter((v) => v.id !== dragging);
            const overId = snapToVertex(others, base.x, base.y);
            const target = overId !== null ? others.find((v) => v.id === overId) : base;
            return {
              ...w,
              vertices: w.vertices.map((v) =>
                v.id === dragging ? { ...v, x: target.x, y: target.y } : v,
              ),
            };
          });
        } else {
          setNodes((prev) =>
            prev.map((n) => (n.id === dragging ? { ...n, x: pt.x, y: pt.y } : n)),
          );
        }
        return;
      }

      if (boxSelect) {
        didDragRef.current = true;
        setBoxSelect((b) => (b ? { ...b, x: pt.x, y: pt.y } : b));
        return;
      }

      if (multiDragging && multiDragRef.current) {
        didDragRef.current = true;
        const md = multiDragRef.current;
        let dx = pt.x - md.startCursor.x;
        let dy = pt.y - md.startCursor.y;
        if (!e.shiftKey) {
          // Grid-snap the delta first so the group lands on grid.
          dx = Math.round(dx / GRID) * GRID;
          dy = Math.round(dy / GRID) * GRID;
          // Then look for any moved vertex whose grid-snapped position
          // would land within snap radius of a stationary vertex; if
          // found, adjust the whole delta so that pair coincides
          // exactly. This is what makes tiled cells click into place.
          let bestD = SNAP_RADIUS;
          let bestSnap = null;
          for (const [vid, start] of md.startPositions) {
            const cx = start.x + dx;
            const cy = start.y + dy;
            for (const sv of wire.vertices) {
              if (md.startPositions.has(sv.id)) continue;
              const d = Math.hypot(sv.x - cx, sv.y - cy);
              if (d < bestD) {
                bestD = d;
                bestSnap = { dx: sv.x - start.x, dy: sv.y - start.y };
              }
            }
          }
          if (bestSnap) {
            dx = bestSnap.dx;
            dy = bestSnap.dy;
          }
        }
        setWire((w) => ({
          ...w,
          vertices: w.vertices.map((v) => {
            const start = md.startPositions.get(v.id);
            if (!start) return v;
            return { ...v, x: start.x + dx, y: start.y + dy };
          }),
        }));
        return;
      }

      if (draggingGroundId !== null) {
        didDragRef.current = true;
        setWire((w) => {
          const g = (w.grounds ?? []).find((gg) => gg.id === draggingGroundId);
          if (!g) return w;
          const v = w.vertices.find((vv) => vv.id === g.vertexId);
          if (!v) return w;
          const off = snapGroundOffset(v.x, v.y, pt.x, pt.y, e.shiftKey);
          return setGroundOffset(w, draggingGroundId, off.dx, off.dy);
        });
        return;
      }

      if (draggingComponentId !== null) {
        didDragRef.current = true;
        // Translate the component rigidly: move its center, keep its
        // orientation. A component must only rotate when its underlying
        // wiring does (i.e. when its endpoint vertices move) — never to
        // align itself with some unrelated wire it happens to pass near
        // (dropping never connected it to that wire anyway). Shift
        // suppresses grid snapping for free-form placement.
        const off = componentDragOffset.current;
        const rawX = pt.x - off.x;
        const rawY = pt.y - off.y;
        const center = e.shiftKey ? { x: rawX, y: rawY } : snapToGrid(rawX, rawY);
        setWire((w) => moveComponentCenter(w, draggingComponentId, center.x, center.y));
        return;
      }

      if (mode === 'wire') {
        setHover(hoverFor(pt, e.shiftKey));
        if (placingGroundFor !== null) {
          setGroundCursor({ x: pt.x, y: pt.y, shiftKey: e.shiftKey });
        }
      }
    },
    [dragging, draggingComponentId, draggingGroundId, multiDragging, boxSelect, wire, panZoom, panel, leftPanel, mode, hoverFor, placingGroundFor],
  );

  const onMouseUp = useCallback(() => {
    panZoom.endPan();
    if (boxSelect) {
      const minX = Math.min(boxSelect.startX, boxSelect.x);
      const maxX = Math.max(boxSelect.startX, boxSelect.x);
      const minY = Math.min(boxSelect.startY, boxSelect.y);
      const maxY = Math.max(boxSelect.startY, boxSelect.y);
      const inside = (v) =>
        v.x >= minX && v.x <= maxX && v.y >= minY && v.y <= maxY;
      const vSet = new Set(wire.vertices.filter(inside).map((v) => v.id));
      const items = [];
      for (const id of vSet) items.push({ kind: 'wireVertex', id });
      for (const w of wire.wires) {
        if (vSet.has(w.from) && vSet.has(w.to)) items.push({ kind: 'wire', id: w.id });
      }
      for (const c of wire.components) {
        if (vSet.has(c.from) && vSet.has(c.to)) items.push({ kind: 'wireComponent', id: c.id });
      }
      if (boxSelect.additive) {
        setSelection((prev) => {
          const have = new Set(prev.map((s) => `${s.kind ?? s.type}:${s.id}`));
          return [...prev, ...items.filter((it) => !have.has(`${it.kind}:${it.id}`))];
        });
      } else {
        setSelection(items);
      }
      setBoxSelect(null);
      return;
    }
    if (multiDragging && multiDragRef.current && mode === 'wire') {
      // Fuse any moved vertex now coincident with a stationary one.
      // Same gluCoincidentVertices call used for single-vertex drag —
      // it handles the wire-separation invariant when two component
      // endpoints would otherwise share a vertex.
      const md = multiDragRef.current;
      const movedIds = new Set(md.startPositions.keys());
      setWire((w) => {
        const matches = [];
        for (const mid of movedIds) {
          const mv = w.vertices.find((v) => v.id === mid);
          if (!mv) continue;
          const target = w.vertices.find(
            (v) =>
              !movedIds.has(v.id) &&
              Math.hypot(v.x - mv.x, v.y - mv.y) < 0.5,
          );
          if (target) matches.push({ movedId: mid, intoId: target.id });
        }
        if (matches.length === 0) return w;

        // Dedupe overlapping components: if a moving component's
        // endpoints both glue onto a stationary component of the
        // same type spanning the same edge, drop the moving one
        // before the vertex glue runs. Without this the wire-
        // separation rule would keep both as parallel edges joined
        // by a zero-length wire — fine for mismatched types, wrong
        // when the user is tiling identical cells.
        const intoMap = new Map(matches.map((m) => [m.movedId, m.intoId]));
        const stationaryByEdge = new Map();
        for (const c of w.components) {
          if (movedIds.has(c.from) || movedIds.has(c.to)) continue;
          const lo = Math.min(c.from, c.to);
          const hi = Math.max(c.from, c.to);
          stationaryByEdge.set(`${c.type}:${lo}-${hi}`, c.id);
        }
        const droppedComponentIds = new Set();
        for (const c of w.components) {
          if (!movedIds.has(c.from) && !movedIds.has(c.to)) continue;
          const mFrom = intoMap.get(c.from) ?? c.from;
          const mTo = intoMap.get(c.to) ?? c.to;
          if (mFrom === c.from && mTo === c.to) continue;
          const lo = Math.min(mFrom, mTo);
          const hi = Math.max(mFrom, mTo);
          if (stationaryByEdge.has(`${c.type}:${lo}-${hi}`)) {
            droppedComponentIds.add(c.id);
          }
        }
        let next =
          droppedComponentIds.size > 0
            ? { ...w, components: w.components.filter((c) => !droppedComponentIds.has(c.id)) }
            : w;
        for (const m of matches) next = gluCoincidentVertices(next, m.movedId, m.intoId);
        // Update the selection: any vertex that was absorbed is
        // replaced by the stationary it merged into; wires/components
        // de-duped by the merge are dropped.
        const absorbed = new Map(matches.map((m) => [m.movedId, m.intoId]));
        const validV = new Set(next.vertices.map((v) => v.id));
        const validW = new Set(next.wires.map((wr) => wr.id));
        const validC = new Set(next.components.map((c) => c.id));
        setSelection((prev) => {
          const out = [];
          const seen = new Set();
          for (const s of prev) {
            let item = s;
            if (s.kind === 'wireVertex') {
              const into = absorbed.get(s.id);
              if (into !== undefined) item = { kind: 'wireVertex', id: into };
              else if (!validV.has(s.id)) continue;
            } else if (s.kind === 'wire' && !validW.has(s.id)) continue;
            else if (s.kind === 'wireComponent' && !validC.has(s.id)) continue;
            const key = `${item.kind ?? item.type}:${item.id}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(item);
          }
          return out;
        });
        return next;
      });
    } else if (dragging !== null && mode === 'wire') {
      const draggedId = dragging;
      setWire((w) => {
        const dv = w.vertices.find((v) => v.id === draggedId);
        if (!dv) return w;
        const overlap = w.vertices.find(
          (v) => v.id !== draggedId && Math.hypot(v.x - dv.x, v.y - dv.y) < 0.5,
        );
        if (!overlap) return w;
        setSelected({ kind: 'wireVertex', id: overlap.id });
        return gluCoincidentVertices(w, draggedId, overlap.id);
      });
    }
    setDragging(null);
    setDraggingComponentId(null);
    setDraggingGroundId(null);
    setMultiDragging(false);
    multiDragRef.current = null;
  }, [panZoom, dragging, multiDragging, mode, boxSelect, wire, setSelected]);

  const onMouseLeave = useCallback(() => {
    panZoom.endPan();
    setDragging(null);
    setDraggingComponentId(null);
    setDraggingGroundId(null);
    setMultiDragging(false);
    multiDragRef.current = null;
    setBoxSelect(null);
    setHover(null);
  }, [panZoom]);

  const onCanvasDoubleClick = useCallback(
    (e) => {
      if (mode !== 'wire') return;
      const pt = panZoom.svgPoint(e);
      const wireHit = snapToWire(wire, pt.x, pt.y, undefined, null, vById);
      if (!wireHit) return;
      // Avoid double-clicking right on top of an existing vertex.
      if (snapToVertex(wire.vertices, pt.x, pt.y) !== null) return;
      e.stopPropagation();
      const r = splitWireAt(wire, wireHit.id, wireHit.t);
      setWire(r.wire);
      if (r.vertexId !== null) {
        setSelected({ kind: 'wireVertex', id: r.vertexId });
      }
    },
    [mode, panZoom, wire, vById],
  );

  const onContextMenu = useCallback((e) => {
    e.preventDefault();
    setSelectedTool(null);
    setConnectFrom(null);
    setDrawingFromVertexId(null);
    setPlacingGroundFor(null);
    setGroundCursor(null);
    setHover(null);
  }, []);

  /** Start a multi-item drag if the grabbed item is part of a 2+
   *  selection. Returns true when multi-drag was started. */
  const startMultiDrag = useCallback(
    (item, cursorPt) => {
      if (selection.length < 2) return false;
      const inSelection = selection.some(
        (s) => (s.kind ?? s.type) === item.kind && s.id === item.id,
      );
      if (!inSelection) return false;
      const vIds = new Set();
      for (const s of selection) {
        if (s.kind === 'wireVertex') vIds.add(s.id);
      }
      const wIdSet = new Set(selection.filter((s) => s.kind === 'wire').map((s) => s.id));
      const cIdSet = new Set(
        selection.filter((s) => s.kind === 'wireComponent').map((s) => s.id),
      );
      for (const w of wire.wires) {
        if (wIdSet.has(w.id)) {
          vIds.add(w.from);
          vIds.add(w.to);
        }
      }
      for (const c of wire.components) {
        if (cIdSet.has(c.id)) {
          vIds.add(c.from);
          vIds.add(c.to);
        }
      }
      if (vIds.size === 0) return false;
      const startPositions = new Map();
      for (const v of wire.vertices) {
        if (vIds.has(v.id)) startPositions.set(v.id, { x: v.x, y: v.y });
      }
      multiDragRef.current = { startCursor: cursorPt, startPositions };
      setMultiDragging(true);
      return true;
    },
    [selection, wire],
  );

  const onWireVertexMouseDown = useCallback(
    (vertexId, e) => {
      if (e.button !== 0) return;
      // Ctrl is reserved for the box-select rectangle started at the
      // canvas level — let the event bubble there.
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      setHover(null);
      // Shift-click is reserved for the multi-selection toggle in
      // handleWireClick — leave the existing selection and highlights
      // alone here so we don't wipe them before the click fires.
      if (e.shiftKey) {
        setDragging(vertexId);
        return;
      }
      // Multi-drag if this vertex is part of an existing multi-select.
      if (startMultiDrag({ kind: 'wireVertex', id: vertexId }, panZoom.svgPoint(e))) {
        return;
      }
      setDragging(vertexId);
      setSelected({ kind: 'wireVertex', id: vertexId });
      const owner = wireNodes.find((n) => n.vertexIds?.includes(vertexId));
      if (owner) handleHighlightNode(owner.id);
      else clearHighlights();
    },
    [wireNodes, setSelected, handleHighlightNode, clearHighlights, startMultiDrag, panZoom],
  );

  const onWireGroundClick = useCallback(
    (groundId) => {
      // Clicking an existing ⏚ while the GND tool is active means
      // "ground this node again" — refuse and flash instead.
      if (selectedTool === 'GND' && placingGroundFor === null) {
        flashGround(groundId);
      }
    },
    [selectedTool, placingGroundFor, flashGround],
  );

  const onWireGroundMouseDown = useCallback(
    (groundId, e) => {
      if (e.button !== 0) return;
      // Active tool — leave the click to the canvas-level handler so
      // GND etc. keep working when the cursor passes over a glyph.
      if (selectedTool) return;
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      setHover(null);
      if (e.shiftKey) {
        toggleSelection({ kind: 'wireGround', id: groundId });
        return;
      }
      setDraggingGroundId(groundId);
      setSelected({ kind: 'wireGround', id: groundId });
    },
    [selectedTool, toggleSelection, setSelected],
  );

  const onWireComponentMouseDown = useCallback(
    (componentId, e) => {
      if (e.button !== 0) return;
      if (selectedTool) return;
      // Ctrl is reserved for the box-select rectangle.
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      setHover(null);
      // Multi-drag if this component is part of an existing multi-select.
      // Skip the per-component drag-offset bookkeeping in that case —
      // the multi-drag handler tracks cursor delta directly.
      if (!e.shiftKey && startMultiDrag({ kind: 'wireComponent', id: componentId }, panZoom.svgPoint(e))) {
        return;
      }
      const c = wire.components.find((cc) => cc.id === componentId);
      if (c) {
        const a = wire.vertices.find((v) => v.id === c.from);
        const b = wire.vertices.find((v) => v.id === c.to);
        if (a && b) {
          const cx = (a.x + b.x) / 2;
          const cy = (a.y + b.y) / 2;
          const pt = panZoom.svgPoint(e);
          componentDragOffset.current = { x: pt.x - cx, y: pt.y - cy };
        }
      }
      setDraggingComponentId(componentId);
      // Shift-click toggles into the multi-selection via the click
      // handler — keep state untouched here.
      if (e.shiftKey) return;
      setSelected({ kind: 'wireComponent', id: componentId });
      handleHighlightComponent(componentId);
    },
    [selectedTool, wire, panZoom, setSelected, handleHighlightComponent, startMultiDrag],
  );

  // ---- render ----

  const isWireMode = mode === 'wire';
  const analysisNodes = isWireMode ? wireNodes : nodes;
  const analysisEdges = isWireMode ? wireEdges : edges;
  const analysisUpdater = isWireMode ? updateWireNode : updateNode;

  return (
    <div style={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <HeaderBar
        onClear={clearCircuit}
        onExport={() => {
          const view = {
            theme,
            showEdgeLabels,
            pan: panZoom.pan,
            zoom: panZoom.zoom,
          };
          downloadJSON(
            analysisNodes,
            analysisEdges,
            isWireMode
              ? { wire, wireNodes, view }
              : { schematicNodes: nodes, view },
          );
        }}
        onImport={handleImportFile}
        onOpenHelp={() => setHelpOpen(true)}
        onSubmitForVerification={() => setSubmitOpen(true)}
      />

      <Toolbar
        mode={mode}
        onSetMode={handleSetMode}
        selectedTool={selectedTool}
        onSelectTool={handleSelectTool}
        connectFrom={connectFrom}
        drawingFromVertexId={drawingFromVertexId}
        placingGroundFor={placingGroundFor}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        onRotate={rotateSelectionBy}
        canRotate={isWireMode && selection.length > 0}
        showEdgeLabels={showEdgeLabels}
        onToggleLabels={() => setShowEdgeLabels((v) => !v)}
        onResetView={() => panZoom.fitToNodes(isWireMode ? wire.vertices : nodes)}
      />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <NodeLabelsPanel
          nodes={analysisNodes}
          onUpdateNode={analysisUpdater}
          components={isWireMode ? wire.components : undefined}
          onUpdateComponent={isWireMode ? onUpdateComponent : undefined}
          width={leftPanel.width}
          onResizeStart={leftPanel.onResizeStart}
          highlightedNodeId={isWireMode ? effectiveHighlightedNodeId : null}
          highlightedComponentId={isWireMode ? effectiveHighlightedComponentId : null}
          onHighlightNode={isWireMode ? handleHighlightNode : undefined}
          onFocusItem={isWireMode ? handleFocusItem : undefined}
          onBlurItem={isWireMode ? handleBlurItem : undefined}
          onSetAllNodeColors={isWireMode ? setAllNodeColors : undefined}
          onSetAllComponentColorsOfType={isWireMode ? setAllComponentColorsOfType : undefined}
          onSetAllComponentColors={isWireMode ? setAllComponentColors : undefined}
          onRelabelAllNodes={isWireMode ? relabelAllNodes : undefined}
          onRelabelComponentsOfType={isWireMode ? relabelComponentsOfType : undefined}
          onReorderNodes={isWireMode ? reorderWireNodes : undefined}
          onReorderComponentsOfType={isWireMode ? reorderComponentsOfType : undefined}
          hideGroundedLabels={hideGroundedLabels}
          onToggleHideGroundedLabels={
            isWireMode ? () => setHideGroundedLabels((v) => !v) : undefined
          }
          hideVertexDots={hideVertexDots}
          onToggleHideVertexDots={
            isWireMode ? () => setHideVertexDots((v) => !v) : undefined
          }
          collapsed={leftCollapsed}
          onToggleCollapsed={() => setLeftCollapsed((v) => !v)}
        />

        <div style={{ flex: 1, position: 'relative' }}>
          <svg
            ref={svgRef}
            width="100%"
            height="100%"
            style={{
              background: 'var(--bg-secondary)',
              cursor: selectedTool ? 'crosshair' : 'default',
            }}
            onMouseDown={(e) => {
              if (document.activeElement && document.activeElement !== document.body) {
                document.activeElement.blur();
              }
              // Ctrl+left starts a box-select rectangle. The vertex /
              // component handlers already opt out when Ctrl is held,
              // so this branch fires whether the cursor is over an
              // item or empty space. Shift extends instead of replaces.
              if (e.button === 0 && (e.ctrlKey || e.metaKey) && isWireMode) {
                e.preventDefault();
                const pt = panZoom.svgPoint(e);
                setBoxSelect({
                  startX: pt.x,
                  startY: pt.y,
                  x: pt.x,
                  y: pt.y,
                  additive: e.shiftKey,
                });
                return;
              }
              panZoom.onCanvasMouseDown(e);
            }}
            onClick={onCanvasClick}
            onDoubleClick={onCanvasDoubleClick}
            onContextMenu={onContextMenu}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
          >
            <defs>
              <pattern
                id="grid"
                width="30"
                height="30"
                patternUnits="userSpaceOnUse"
                patternTransform={`translate(${panZoom.pan.x},${panZoom.pan.y}) scale(${panZoom.zoom})`}
              >
                <path
                  d="M 30 0 L 0 0 0 30"
                  fill="none"
                  stroke="var(--grid-line)"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />

            <g transform={`translate(${panZoom.pan.x},${panZoom.pan.y}) scale(${panZoom.zoom})`}>
              {isWireMode ? (
                <WireLayer
                  wire={wire}
                  wireNodes={wireNodes}
                  drawingFromVertexId={drawingFromVertexId}
                  hover={hover}
                  selectedTool={selectedTool}
                  selection={selection}
                  onVertexMouseDown={onWireVertexMouseDown}
                  onComponentMouseDown={onWireComponentMouseDown}
                  onGroundMouseDown={onWireGroundMouseDown}
                  onGroundClick={onWireGroundClick}
                  flashingGroundId={flashingGroundId}
                  showLabels={showEdgeLabels}
                  zoom={panZoom.zoom}
                  highlightedNodeId={effectiveHighlightedNodeId}
                  highlightedComponentId={effectiveHighlightedComponentId}
                  onHighlightNode={handleHighlightNode}
                  onHighlightComponent={handleHighlightComponent}
                  placingGroundFor={placingGroundFor}
                  cursor={groundCursor}
                  shiftKey={groundCursor?.shiftKey ?? false}
                  hideGroundedLabels={hideGroundedLabels}
                  hideVertexDots={hideVertexDots}
                />
              ) : (
                <>
                  {edges.map((edge) => {
                    const from = nodes.find((n) => n.id === edge.from);
                    const to = nodes.find((n) => n.id === edge.to);
                    if (!from || !to) return null;
                    return (
                      <CircuitEdge
                        key={edge.id}
                        edge={edge}
                        from={from}
                        to={to}
                        isSelected={selected?.type === 'edge' && selected.id === edge.id}
                        showLabel={showEdgeLabels}
                        onClick={onSchematicEdgeClick}
                      />
                    );
                  })}
                  {nodes.map((node) => (
                    <CircuitNode
                      key={node.id}
                      node={node}
                      isSelected={selected?.type === 'node' && selected.id === node.id}
                      isSource={connectFrom === node.id}
                      hasToolActive={!!selectedTool}
                      onClick={onSchematicNodeClick}
                      onMouseDown={onSchematicNodeMouseDown}
                    />
                  ))}
                </>
              )}
              {boxSelect && (
                <rect
                  x={Math.min(boxSelect.startX, boxSelect.x)}
                  y={Math.min(boxSelect.startY, boxSelect.y)}
                  width={Math.abs(boxSelect.x - boxSelect.startX)}
                  height={Math.abs(boxSelect.y - boxSelect.startY)}
                  fill="var(--accent-blue)"
                  fillOpacity={0.1}
                  stroke="var(--accent-blue)"
                  strokeWidth={1.5 / Math.max(panZoom.zoom, 0.0001)}
                  strokeDasharray={`${4 / Math.max(panZoom.zoom, 0.0001)} ${3 / Math.max(panZoom.zoom, 0.0001)}`}
                  pointerEvents="none"
                />
              )}
            </g>
          </svg>

          {isWireMode ? (
            <WirePropertiesPanel
              selected={selected}
              wire={wire}
              onSetComponentType={onSetComponentType}
              onSetComponentValue={onSetComponentValue}
              onDelete={deleteSelected}
              onMergeVertex={mergeSelectedVertex}
              canMergeVertex={
                selected?.kind === 'wireVertex'
                  ? canMergeVertexIntoWire(wire, selected.id)
                  : false
              }
            />
          ) : (
            <PropertiesPanel
              selected={selected}
              nodes={nodes}
              edges={edges}
              onUpdateNode={updateNode}
              onUpdateEdge={updateEdge}
              onDelete={deleteSelected}
            />
          )}
        </div>

        <HamiltonianPanel
          nodes={analysisNodes}
          edges={analysisEdges}
          width={panel.width}
          onResizeStart={panel.onResizeStart}
          collapsed={rightCollapsed}
          onToggleCollapsed={() => setRightCollapsed((v) => !v)}
        />
      </div>

      <HelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onRunTutorial={() => {
          setHelpOpen(false);
          setTutorialOpen(true);
        }}
      />
      <Tutorial
        open={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
        onDismissForever={dismissTutorialForever}
      />
      <SubmitModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        nodes={analysisNodes}
        edges={analysisEdges}
        extra={
          isWireMode
            ? { wire, wireNodes, view: { theme, showEdgeLabels, pan: panZoom.pan, zoom: panZoom.zoom } }
            : { schematicNodes: nodes, view: { theme, showEdgeLabels, pan: panZoom.pan, zoom: panZoom.zoom } }
        }
      />
    </div>
  );
}
